import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CustomRule } from 'devextreme-react/form';
import { DropDownOptions } from 'devextreme-react/lookup';

import { AppFieldBox } from 'components/appComponents';
import ClaimsProvider from 'components/claimsBlock';
import { useAuth } from 'context/auth';
import { SelectBox, TextBox, Validator } from 'devextreme-react';
import DataSource from 'devextreme/data/data_source';

import { useLazyGetContractorObjectGroupsQuery } from 'bus/contractorObject/services';
import { ContractorObjectGroups } from 'bus/contractorObject/types';

import CreateObjectModal from './modals/create';

export type ObjectChangeType = {
  objectId: number | null;
  fault: number;
};

type ObjectsProps = {
  value: number | null;
  contractorId: number | null;
  methodOfReceptionType: number;
  readOnly: boolean;
  onChange: (value: ObjectChangeType) => void;
  contractorType: number | null;
  //setObjectInfo: (objectContractor: ContractorObjectGroups) => void;
};

const Objects: FC<ObjectsProps> = ({
  value,
  readOnly,
  onChange,
  contractorId,
  contractorType,
  methodOfReceptionType,
}) => {
  const { getClaims }: any = useAuth();
  const [getContractorObjectGroups, { data: contractorObjectGroupData }] = useLazyGetContractorObjectGroupsQuery();
  const [showModalCreate, setShowModalCreate] = useState(false);

  const isLegalValidate = (e: any) => {
    if (methodOfReceptionType === 0) {
      return true;
    }
    if (contractorType !== 0) {
      return true;
    } else {
      return e.value;
    }
  };

  let selectBoxRef = useRef<SelectBox | null>(null);

  const storeObject: DataSource = useMemo(
    () =>
      new DataSource({
        store: contractorObjectGroupData,
        key: 'id',
        group: 'objectName',
      }),
    [contractorObjectGroupData],
  );

  const getContractorObject = useCallback(async () => {
    if (contractorId) {
      const params = {
        contractorId,
        ...(value && { isRequiredId: value }),
      };
      await getContractorObjectGroups(params);
      storeObject.reload();
    }
  }, [contractorId, getContractorObjectGroups, storeObject, value]);

  const hideModalHandler = useCallback(() => {
    getContractorObject();
    setShowModalCreate(false);
  }, [getContractorObject]);

  useEffect(() => {
    getContractorObject();
  }, [contractorId, getContractorObject]);

  // Возвращаемое значение обязательно должно содержать TextBox
  // https://js.devexpress.com/React/Documentation/ApiReference/UI_Components/dxSelectBox/Configuration/#fieldRender
  const fieldRender = useCallback((data: ContractorObjectGroups) => {
    return (
      <div>
        {data && data.objectName && <b>{data.objectName}: </b>}
        <TextBox style={{ display: 'inline-block' }} value={data && data.name} />
      </div>
    );
  }, []);

  const searchExpr = useMemo(() => ['name', 'objectName'], []);

  return (
    <>
      <AppFieldBox label="Объект" labelTop>
        <div className="d-flex align-center">
          <SelectBox
            stylingMode="underlined"
            readOnly={readOnly}
            style={{ width: '100%' }}
            dataSource={storeObject}
            displayExpr="name"
            valueExpr="id"
            searchEnabled={true}
            fieldRender={fieldRender}
            searchExpr={searchExpr}
            value={value}
            grouped={true}
            onItemClick={(e) => {
              onChange({
                objectId: e.itemData.id,
                fault: e.itemData.fault,
              });
              //setObjectInfo(e.itemData);
            }}
            ref={selectBoxRef}
            buttons={[
              'dropDown',
              {
                name: 'cleare',
                location: 'after',
                options: {
                  onClick: () => onChange({ objectId: null, fault: 0 }),
                  icon: 'close',
                  visible: getClaims(['CanEditObject']),
                  disabled: !contractorId,
                },
              },
              {
                name: 'createObject',
                location: 'after',
                options: {
                  onClick: () => setShowModalCreate(!showModalCreate),
                  icon: 'plus',
                  visible: getClaims(['CanEditObject']),
                  disabled: !contractorId,
                },
              },
            ]}
          >
            <DropDownOptions title="Выберите объект" closeOnOutsideClick={true} showTitle={false} />
            <Validator>
              <CustomRule message="Выберите объект" validationCallback={isLegalValidate} />
            </Validator>
          </SelectBox>
          <ClaimsProvider claimsBlock={['CanEditObject']}>
            {showModalCreate && (
              <CreateObjectModal
                id={contractorId}
                refresh={getContractorObject}
                visible={showModalCreate}
                changeVisible={hideModalHandler}
              />
            )}
          </ClaimsProvider>
        </div>
      </AppFieldBox>
    </>
  );
};

export default memo(Objects, (prev, next) => {
  return prev.contractorId === next.contractorId && prev.value === next.value && prev.onChange === next.onChange;
});
